import { Divider, Flex } from '@chakra-ui/react';
import { useStoreApi, useReactFlow } from 'react-flow-renderer';

import { accent, neutral, radius, shadow } from 'styles/revampTokens';

import FigmaZoomInIcon from '../Icons/FigmaZoomInIcon';
import FigmaZoomOutIcon from '../Icons/FigmaZoomOutIcon';
import FigmaFitViewIcon from '../Icons/FigmaFitViewIcon';
import FigmaMapLockIcon from '../Icons/FigmaMapLockIcon';
import FigmaTreeLayoutIcon from '../Icons/FigmaTreeLayoutIcon';

import type { ReactNode } from 'react';

// Locked reads as a warning/caution state (layout is frozen), which is why
// it gets its own amber accent rather than the purple used for the other
// toggled controls (vertical/horizontal layout) below.
const LOCK_COLOR = '#F59E0B';
const LOCK_TINT = '#FEF3C7';

interface Properties {
  layoutDirection: string;
  onLayout: (layout: string) => void;
  isDraggable: any;
  setIsDraggable: (parameter: boolean | ((previous: boolean) => boolean)) => void;
}

/**
 * One elevated pill instead of six separately-bordered squares — matches the
 * Figma map-view toolbar (75644:18516), which groups every control behind a
 * single card with hairline dividers between cells rather than a border per
 * button. Active/selected state now reads through the revamp `accent` token
 * rather than the module's old hardcoded teal (#4BB6B9), matching the
 * "accent only where the user can act" rule the rest of Builder Mode follows.
 */
const ControlButton = ({
  title,
  onClick,
  isActive,
  activeBg = accent.subtle,
  children,
}: {
  title: string;
  onClick: () => void;
  isActive?: boolean;
  activeBg?: string;
  children: ReactNode;
}) => (
  <Flex
    as='button'
    type='button'
    title={title}
    aria-label={title}
    onClick={onClick}
    w='44px'
    h='44px'
    alignItems='center'
    justifyContent='center'
    bg={isActive ? activeBg : 'transparent'}
    cursor='pointer'
    transition='background 0.12s ease'
    _hover={{ bg: isActive ? activeBg : neutral.surfaceHover }}
  >
    {children}
  </Flex>
);

const MapControls = ({ layoutDirection, onLayout, isDraggable, setIsDraggable }: Properties) => {
  const store = useStoreApi();
  const { zoomIn, zoomOut, setCenter } = useReactFlow();

  const focusNode = () => {
    const { nodeInternals } = store.getState();
    const nodes = Array.from(nodeInternals).map(([, node]) => node);

    if (nodes.length > 0) {
      const node = nodes[0];

      const { x } = node.position;
      const y = node.position.y + (node?.height ? node.height / 0.5 : 0);
      const zoom = 1;

      setCenter(x, y, { zoom, duration: 500 });
    }
  };

  const toggleInteractivity = () => {
    setIsDraggable((previousState: any) => !previousState);
  };

  return (
    <Flex
      direction='column'
      position='absolute'
      left='16px'
      top='50%'
      transform='translateY(-50%)'
      zIndex={10}
      bg={neutral.surface}
      border='1px solid'
      borderColor={neutral.border}
      borderRadius={radius.surface}
      boxShadow={shadow.overlay}
      overflow='hidden'
    >
      <ControlButton title='Zoom in' onClick={() => zoomIn({ duration: 500 })}>
        <FigmaZoomInIcon color={neutral.borderStrong} />
      </ControlButton>
      <Divider borderColor={neutral.borderSubtle} />

      <ControlButton title='Zoom out' onClick={() => zoomOut({ duration: 500 })}>
        <FigmaZoomOutIcon color={neutral.borderStrong} />
      </ControlButton>
      <Divider borderColor={neutral.borderSubtle} />

      <ControlButton title='Fit view' onClick={focusNode}>
        <FigmaFitViewIcon color={neutral.borderStrong} />
      </ControlButton>
      <Divider borderColor={neutral.borderSubtle} />

      <ControlButton
        title={isDraggable ? 'Lock layout' : 'Unlock layout'}
        onClick={toggleInteractivity}
        isActive={!isDraggable}
        activeBg={LOCK_TINT}
      >
        <FigmaMapLockIcon color={!isDraggable ? LOCK_COLOR : neutral.borderStrong} />
      </ControlButton>
      <Divider borderColor={neutral.borderSubtle} />

      <ControlButton
        title='Vertical layout'
        onClick={() => onLayout('TB')}
        isActive={layoutDirection === 'TB'}
      >
        <FigmaTreeLayoutIcon
          rotate={180}
          color={layoutDirection === 'TB' ? accent.solid : neutral.borderStrong}
        />
      </ControlButton>
      <Divider borderColor={neutral.borderSubtle} />

      <ControlButton
        title='Horizontal layout'
        onClick={() => onLayout('LR')}
        isActive={layoutDirection === 'LR'}
      >
        <FigmaTreeLayoutIcon
          rotate={-90}
          color={layoutDirection === 'LR' ? accent.solid : neutral.borderStrong}
        />
      </ControlButton>
    </Flex>
  );
};

export default MapControls;
